'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import { useCallback, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'

type FooterLink = {
  label: string
  targetId?: string
  href?: string
}

const FOOTER_LINKS: FooterLink[] = [
  { label: 'LEAGUES', targetId: 'landing-leagues' },
  { label: 'ABOUT', targetId: 'landing-about' },
  { label: 'HOW TO', targetId: 'landing-how-to' },
  { label: 'SPONSORS & PARTNERS', targetId: 'landing-sponsors' },
  { label: 'CONTACT US', href: '/contact' },
  { label: 'TERMS OF SERVICE', href: '/terms-of-service' },
  { label: 'PRIVACY POLICY', href: '/privacy-policy' }
]

const linkBaseClasses =
  'text-[0.65rem] sm:text-[0.7rem] md:text-[0.8rem] font-bold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:text-white/80 whitespace-nowrap'

export default function LandingFooter() {
  const currentYear = useMemo(() => new Date().getFullYear(), [])
  const pathname = usePathname()
  const router = useRouter()

  const handleScrollToSection = useCallback(
    (targetId: string) => {
      if (pathname === '/') {
        const section = document.getElementById(targetId)
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } else {
        router.push(`/#${targetId}`)
      }
    },
    [pathname, router]
  )

  return (
    <footer
      className="relative mt-8 md:mt-16 text-white"
      style={{
        backgroundColor: 'var(--neu-surface)'
      }}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-70" />

      <div className="relative flex w-full flex-col gap-8 px-4 sm:px-8 md:px-12 py-6 md:py-10 shadow-neumorphic lg:flex-row lg:items-center lg:justify-between xl:px-16">
        <div className="flex flex-1 flex-col items-center gap-4 text-center lg:items-start lg:text-left">
          <div className="flex items-center gap-5">
            <Image
              src="/assets/Logos/NPFCBlackLogo.png"
              alt="NPFC crest"
              width={115}
              height={48}
              className="h-8 md:h-12 object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.45)] block dark:hidden"
              style={{ width: 'auto' }}
            />
            <Image
              src="/assets/Logos/NPFCwhiteLogo.png"
              alt="NPFC crest"
              width={115}
              height={48}
              className="h-8 md:h-12 object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.45)] hidden dark:block"
              style={{ width: 'auto' }}
            />
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
            {'\u00A9'} {currentYear} NPFC. ALL RIGHTS RESERVED
          </p>
        </div>

        <nav
          className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-4 md:gap-x-8 gap-y-3 md:gap-y-4 text-center lg:flex-nowrap lg:justify-end"
          aria-label="Landing footer navigation"
        >
          {FOOTER_LINKS.map((link) => {
            if (link.targetId) {
              return (
                <button
                  key={link.label}
                  type="button"
                  onClick={() => handleScrollToSection(link.targetId!)}
                  className={linkBaseClasses}
                >
                  {link.label}
                </button>
              )
            }

            if (link.href) {
              return (
                <Link key={link.label} href={link.href} className={linkBaseClasses}>
                  {link.label}
                </Link>
              )
            }

            return null
          })}
        </nav>
      </div>
    </footer>
  )
}
